// TODO: Replace this client with the SDK client once available in the SDK.
import { makeAuthenticatedServiceRequest } from './servicesClient';
import { resolveEffectiveSku } from 'src/util/skuKey';
import type { FetchError } from 'src/types/fetch';
import type {
    DesignDataRemainingOptionsResponseType,
    DesignDataResponseType,
    DesignDataResultType,
    PanelPlacementType,
} from 'src/types/decorationSpecs';

const ENDPOINT = 'https://design-initialization-service.docext.cimpress.io';
const REQUESTER = 'scene-maker';
const DEFAULT_CULTURE = 'en-US';

// Requested in every call so the response shape stays consistent regardless of which fields Scene
// Maker currently parses.
const FIELDS = ['designViews', 'designDocument', 'printWhite', 'decorationPlacement'];

export type RetrieveDesignDataParams = {
    sku: string;
    skuVersion?: number;
    merchantSku?: string;
    merchantVersion?: number;
    /** The product's currently selected merchant variables (e.g. Size, Substrate Color). May be a
     * partial set — DIS reports back which options are still unresolved rather than erroring hard;
     * see the 400-handling below. */
    merchantVariables?: Record<string, string | undefined>;
    culture?: string;
};

function buildDesignDataUrl({
    effectiveSku,
    effectiveVersion,
    culture,
    merchantVariables,
}: {
    effectiveSku: string;
    effectiveVersion: number;
    culture: string;
    merchantVariables: Record<string, string | undefined>;
}): string {
    const encodedSku = encodeURIComponent(effectiveSku);
    const versionSegment = `${encodedSku}/${encodeURIComponent(String(effectiveVersion))}`;

    const params: string[] = [
        `culture=${encodeURIComponent(culture)}`,
        `requester=${encodeURIComponent(REQUESTER)}`,
        'fullBleed=false',
        ...FIELDS.map((field) => `fields=${encodeURIComponent(field)}`),
    ];

    // Pass every currently-selected merchant variable, not a hardcoded subset — DIS's placement
    // and white-layer rules can vary on axes beyond Size/Substrate Color (Deco Area, Backside,
    // Decoration Technology, ...) depending on the product, and under-specified products return a
    // 400 naming exactly which options are still missing (handled below).
    Object.entries(merchantVariables).forEach(([key, value]) => {
        if (!value) {
            return;
        }
        params.push(`optionSelections${encodeURIComponent(`[${key}]`)}=${encodeURIComponent(value)}`);
    });

    return `${ENDPOINT}/v1/designData/products/${versionSegment}?${params.join('&')}`;
}

function mapDesignDataResponse(data?: DesignDataResponseType): DesignDataResultType {
    const additionalData = data?.additionalData;
    const rawPlacement = additionalData?.decorationPlacement;

    const decorationPlacement: PanelPlacementType[] | null = rawPlacement
        ? Object.entries(rawPlacement).map(([panelName, fields]) => ({
              panelName,
              distanceFromCollarToTopMm: fields.DistanceFromCollarToTopMm,
              scaledPrintWidthMm: fields.ScaledPrintWidthMm,
              scaledPrintHeightMm: fields.ScaledPrintHeightMm,
              xOffsetFromCenterMm: fields.XOffsetFromCenterMm,
          }))
        : null;

    return {
        decorationPlacement,
        printWhite: additionalData?.printWhite ?? null,
        missingSelections: null,
    };
}

/**
 * Retrieves apparel decoration-placement and print-white (white-ink underbase) data from the
 * Design Initialization Service, for the given product and its currently selected merchant
 * variables.
 *
 * Accepts either the platform SKU/version or the merchant (PRD) SKU/version — confirmed live that
 * DIS treats both as valid identifiers for the same configured product, so this resolves the
 * effective SKU the same way `retrieveSurfaceDataForProduct` does for Surface Explorer.
 *
 * Resolves to `{ decorationPlacement: null, printWhite: null, missingSelections: null }` for
 * products DIS has no design view for (404 — typically non-apparel, or apparel not routed for this
 * data) rather than throwing, and to a populated `missingSelections` (rather than throwing) when
 * DIS reports the given SKU/version still has unresolved options (400) — expected while a designer
 * hasn't finished picking merchant variables (e.g. Size) yet.
 */
export function retrieveDesignData({
    sku,
    skuVersion,
    merchantSku,
    merchantVersion,
    merchantVariables = {},
    culture = DEFAULT_CULTURE,
}: RetrieveDesignDataParams): Promise<DesignDataResultType> {
    if (!sku) {
        return Promise.reject(new Error(`retrieveDesignData requires a sku, got: sku=${sku}`));
    }

    const { effectiveSku, effectiveVersion } = resolveEffectiveSku({ sku, skuVersion, merchantSku, merchantVersion });
    if (effectiveVersion === undefined) {
        return Promise.reject(new Error(`retrieveDesignData requires a version, got: effectiveSku=${effectiveSku}`));
    }
    const url = buildDesignDataUrl({ effectiveSku, effectiveVersion, culture, merchantVariables });

    return makeAuthenticatedServiceRequest<DesignDataResponseType>({ url }).then(
        (response) => mapDesignDataResponse(response?.data),
        (error: FetchError) => {
            const status = error?.response?.status;

            if (status === 404) {
                return { decorationPlacement: null, printWhite: null, missingSelections: null };
            }

            if (status === 400) {
                const details = (error.response?.data as DesignDataRemainingOptionsResponseType)?.details;
                const missingSelections = [
                    ...(details?.remainingOptions ?? []),
                    ...(details?.remainingVariables ?? []),
                ];
                if (missingSelections.length > 0) {
                    return { decorationPlacement: null, printWhite: null, missingSelections };
                }
            }

            throw error;
        },
    );
}
